/**
 * #427 W2 — date helpers shared by the schedule + time-off surfaces.
 *
 * Design Philosophy: raw ISO timestamps are an anti-pattern on user-facing
 * surfaces ("internal identifiers shown raw to users"). Everything a
 * staffer reads goes through these formatters; the ISO string stays on the
 * wire and in `value=` attributes only.
 *
 * All parsing pins noon local time (`T12:00:00`) rather than midnight —
 * the UTC-parse off-by-one that bit the customer app (#273) comes from
 * `new Date('2026-07-21')` being midnight UTC, which renders as the
 * PREVIOUS day anywhere west of Greenwich.
 */

/** Local `Date` for an ISO `YYYY-MM-DD`, pinned to noon (never UTC-shifted). */
export function parseIsoDay(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

/** "Tue, Jul 21" — the day a staffer reads on a phone. */
export function formatDayShort(iso: string): string {
  return parseIsoDay(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "Jul 21" — compact, for chips/bands where the weekday is already known. */
export function formatDayCompact(iso: string): string {
  return parseIsoDay(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Inclusive range, collapsed when it's a single day:
 *   ('2026-07-21', '2026-07-21') → "Tue, Jul 21"
 *   ('2026-07-21', '2026-07-23') → "Tue, Jul 21 – Thu, Jul 23"
 */
export function formatDayRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return formatDayShort(startIso);
  return `${formatDayShort(startIso)} – ${formatDayShort(endIso)}`;
}

/** Inclusive containment on ISO day strings (lexical compare is safe for YYYY-MM-DD). */
export function isDayWithin(day: string, startIso: string, endIso: string): boolean {
  return day >= startIso && day <= endIso;
}

/** "8:00 AM – 4:00 PM" from the server's 24h `HH:MM` shift times. */
export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatClock(startTime)} – ${formatClock(endTime)}`;
}

function formatClock(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!match) return hhmm;
  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours)) return hhmm;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${minutes} ${suffix}`;
}
